import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  filter,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';
import { IAccountResponseError } from '../../account.data';
import { AccountStore } from '../../store/stores';
import { AccountDispatcher } from '../../store/dispatchers';
import { LocalizationStore } from '../../../localization/store/stores';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';

@Component({
  selector: 'app-set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetPasswordComponent implements OnInit, OnDestroy, AfterViewInit {
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  processing$: Observable<boolean>;
  error$: Observable<string | Error | IAccountResponseError>;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject$: Observable<ITrackingObject>;
  setPasswordAction: ITrackingAction;

  constructor(
    private _accountStore: AccountStore,
    private _accountDispatchers: AccountDispatcher,
    private _localizationStore: LocalizationStore,
    private _trackingService: I2gNgTrackingService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this.trackingScreenName = TrackingScreenNamesEnum.set_password;
    this.trackingObject$ = this._accountStore
      .getTrackingObject()
      .pipe(shareReplay(1));
    this.setPasswordAction = this._trackingActionBuilder.buildButtonTapped(
      'set_password_submit',
      this.trackingScreenName
    );

    this.processing$ = this._accountStore
      .isSetPasswordProcessing()
      .pipe(shareReplay(1));

    this.error$ = this._accountStore.getSetPasswordError().pipe(
      map((error: Error | IAccountResponseError) => {
        if (error) {
          return error instanceof Error ? error.message : error.description;
        }
        return error;
      })
    );
  }

  ngAfterViewInit() {
    this._trackingService.trackPage(this.trackingScreenName);
  }

  setPassword(password: string) {
    this.processing$
      .pipe(
        takeUntil(this._destroyed),
        filter((processing) => !processing),
        flatMap(() =>
          combineLatest(
            this._accountStore.getSetPasswordToken(),
            this._localizationStore.getCurrentLocale()
          )
        ),
        take(1)
      )
      .subscribe(([token, locale]) => {
        this._accountDispatchers.updatePasswordToken(
          token.userId,
          token.value,
          password,
          locale
        );
      });
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
